<template>
  <div class="relative w-full" :class="[overrideHeight ? overrideHeight :'h-[468px]']" >
    <div class="flex justify-end">
      <ExportMenu class="absolute z-10" :exports="[{ type: 'chart', action: downloadSVG }]" />
    </div>
    <div ref="chart"></div>
  </div>
</template>

<script>
import * as d3 from 'd3'
import {toProperCase} from "@/utils/common";
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue'
import { blockSensitiveEgress } from '@/utils/demoEgress'

export default {
  name: 'ChartV4',
  components: { ExportMenu },
  props: {
    overrideHeight:{
      type:String,
      required:false
    },
    chart_data: {
      type: Object,
      required: true,
      default() {
        return {
          series: [
            {
              values:[0],
              config: {
                label: 'Default Series',
                backgroundColor: 'rgba(192, 0, 0, 0.4)',
                type: 'bar',
                yAxis:'left',
                offset:0
              }
            }
          ],
          labels: ['Default']
        }
      }
    },
    chart_config: {
      type: Object,
      default() {
        return {
          chartType: 'vertical_bar',
          margin: { top: 80, right: 60, bottom: 60, left: 80 },
          maxBarWidth: 10, // Add this line to set default max bar width
          xAxis: {
            labelRotation: -45,
            tickFormat: null,
            fontSize: 12
          },
          yAxes: [
            {
              position: 'left',
              tickFormat: null,
              fontSize: 12,
              maxHorizontalLabelLength:40
            },
            {
              position: 'right',
              tickFormat: null,
              fontSize: 12
            }
          ],
          legend: {
            display: true,
            position: 'top',
            spacing: 15  // Add this new property for spacing
          },
          labelDisplayConfig: {
            maxLabels: 40,
            minLabelSpacing: 5,
            labelFontSize:12
          },
          formatting: {
            percentage: true,
            sig_figs: 1
          },
          title: '',
          subtitle: ''
        }
      }
    },
  },
  emits: ['bar-click', 'line-click'],
  data() {
    return {
      svg: null,
      margin: null,
      legendHeight:8
    }
  },
  mounted() {
    this.createChart()
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    truncateLabel(label, maxLength) {
      return label.length > maxLength ? label.slice(0, maxLength) + '...' : label
    },
createChart() {
  d3.select(this.$refs.chart).select('svg').remove()
  const data = this.processData()

  this.margin = this.calculateMargins(data, this.$el.clientWidth, this.$el.clientHeight)

  // Determine if legend will actually be shown
  const willShowLegend = this.chart_config.legend.display && this.chart_data.series.length > 1

  if (willShowLegend && this.chart_config.legend.position === 'right') {
    // Calculate and set legend width
    const legendWidth = this.calculateLegendWidth()
    this.margin.right = legendWidth + 40
  } else {
    // No legend on right, use default right margin
    this.margin.right = 60
  }

  const width = this.$el.clientWidth - this.margin.left - this.margin.right
  const height = this.$el.clientHeight - this.margin.top - this.margin.bottom

  const svgElement = d3.select(this.$refs.chart)
    .append('svg')
    .attr('width', width + this.margin.left + this.margin.right)
    .attr('height', height + this.margin.top + this.margin.bottom)

  if (this.chart_config.title) {
    svgElement.append('text')
      .attr('x', (width + this.margin.left + this.margin.right) / 2)
      .attr('y', 30)
      .attr('text-anchor', 'middle')
      .style('font-size', '20px')
      .style('font-weight', '600')
      .style('font-family', '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif')
      .style('letter-spacing', '-0.02em')
      .text(toProperCase(this.chart_config.title))
  }

  if (this.chart_config.subtitle) {
    svgElement.append('text')
      .attr('x', (width + this.margin.left + this.margin.right) / 2)
      .attr('y', 50)
      .attr('text-anchor', 'middle')
      .style('font-size', '14px')
      .style('font-style', 'italic')
      .text(this.chart_config.subtitle)
  }

  const svg = svgElement
    .append('g')
    .attr('transform', `translate(${this.margin.left},${this.margin.top})`)

  const scales = this.defineScales(data, width, height)
  this.drawBars(svg, scales.xScale, scales.yScales, scales.numSeries, data)
  this.drawLines(svg, scales, data)
  this.drawAxes(svg, scales, width, height, data)
  this.addDataLabels(svg, scales, data)
  this.addTooltip(svg)

  this.addLegend(svg, width)

  this.svg = svg
},
    calculateLegendWidth() {
      const tempSvg = d3.select(this.$refs.chart).append('svg').attr('visibility', 'hidden')
      const tempG = tempSvg.append('g')

      let maxWidth = 0

      this.chart_data.series.forEach((series) => {
        const text = tempG.append('text')
          .style('font-size', '12px')
          .style('font-weight', '500')
          .text(series.config.label)

        const bbox = text.node().getBBox()
        maxWidth = Math.max(maxWidth, bbox.width)
        text.remove()
      })

      tempSvg.remove()

      // Add space for rectangle (18px) + padding between rect and text (6px) + text width
      return maxWidth + 18 + 6
    },
    downloadSVG() {
      if (blockSensitiveEgress('export')) return
      const svgElement = this.$refs.chart.querySelector('svg')
      const serializer = new XMLSerializer()
      let source = serializer.serializeToString(svgElement)

      if (!source.match(/^<svg[^>]+xmlns="http:\/\/www\.w3\.org\/2000\/svg"/)) {
        source = source.replace(
          /^<svg/,
          '<svg xmlns="http://www.w3.org/2000/svg"'
        )
      }
      const blob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'chart.svg'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },
    formatDataValue(value) {
      if (value === null || value === undefined || isNaN(value)) {
        return "N/A";
      }

      if (this.chart_config.formatting.percentage) {
        value = value * 100;
      }

      value = value.toFixed(this.chart_config.formatting.sig_figs);

      if (this.chart_config.formatting.percentage) {
        value = value + '%';
      }

      return value;
    },
    handleResize() {
      this.createChart()
    },
    processData() {
      return this.chart_data.labels.map((label, index) => {
        const dataPoint = { label, index }
        this.chart_data.series.forEach((series) => {
          const value = series.values[index]
          dataPoint[series.config.label] = Number.isFinite(value) ? value : null
        })
        if (this.chart_data.colors && this.chart_data.colors[index]) {
          dataPoint.color = this.chart_data.colors[index]
        }
        if (this.chart_data.filter_exclusions && this.chart_data.filter_exclusions.length > index) {
          const exclusionValue = this.chart_data.filter_exclusions[index]
          dataPoint.excludeFromFilter = exclusionValue === 1 || exclusionValue === true
        } else {
          dataPoint.excludeFromFilter = false
        }
        if (this.chart_data.object_ids && this.chart_data.object_ids.length > index) {
          dataPoint.object_ids = this.chart_data.object_ids[index]
        }
        return dataPoint
      })
    },
    calculateMargins(data, client_width, client_height) {
      let margin = { top: 80, right: 60, bottom: 60, left: 80 }
      if (this.chart_config && this.chart_config.margin) {
        margin = { ...this.chart_config.margin }
      }

      const tempSvg = d3.select(this.$refs.chart).append('svg')
      const g = tempSvg.append('g')

      // Adjust the top margin to add more space between legend and chart
      if (this.chart_config.legend.display && this.chart_config.legend.position === 'subtitle') {
        const legendSpacing = this.chart_config.legend.spacing || 15
        margin.top += legendSpacing  // This will push the chart content down
      }

      if (this.chart_config.chartType === 'horizontal_bar') {
        const yAxisLabels = data.map((d) => d.label)
        const yAxisFontSize = this.chart_config.yAxes[0]?.fontSize || 12
        let maxLabelWidth = 0
        yAxisLabels.forEach((label) => {
          const text = g.append('text')
            .style('font-size', `${yAxisFontSize}px`)
            .text(label)
          const bbox = text.node().getBBox()
          if (bbox.width > maxLabelWidth) {
            maxLabelWidth = bbox.width
          }
          text.remove()
        })
        margin.left = Math.min(maxLabelWidth*1.5,0.6*client_width)
      } else {
        const xAxisLabels = data.map((d) => d.label)
        const xAxisFontSize = this.chart_config.xAxis.fontSize || 12
        const labelRotation = this.chart_config.xAxis.labelRotation || 0
        let maxLabelSize = 0
        xAxisLabels.forEach((label) => {
          const text = g.append('text')
            .style('font-size', `${xAxisFontSize}px`)
            .attr('transform', `rotate(${labelRotation})`)
            .text(label)
          const bbox = text.node().getBBox()
          if (labelRotation === 0) {
            if (bbox.width > maxLabelSize) {
              maxLabelSize = bbox.width
            }
          } else {
            if (bbox.height > maxLabelSize) {
              maxLabelSize = bbox.height
            }
          }
          text.remove()
        })
        margin.bottom = Math.max(margin.bottom, maxLabelSize - 40)
      }

      tempSvg.remove()
      return margin
    },
    defineScales(data, width, height) {
      const numSeries = this.chart_data.series.length
      const yAxesConfig = this.chart_config.yAxes || []

      let xScale = null
      let yScales = {}
      let yMaxLeft = yAxesConfig[0]?.max ?? 1
      let yMaxRight = yAxesConfig[1]?.max ?? 1

      // Group bar series by offset and axis for stacking calculation
      const barSeriesByOffsetAndAxis = {}
      this.chart_data.series.forEach((series) => {
        if (series.config.type !== 'line') {
          const offset = series.config.offset || 0
          const yAxis = series.config.yAxis || 'left'
          const key = `${offset}-${yAxis}`

          if (!barSeriesByOffsetAndAxis[key]) {
            barSeriesByOffsetAndAxis[key] = []
          }

          barSeriesByOffsetAndAxis[key].push(series)
        }
      })

      // Also consider line series for max value calculation
      const lineSeries = this.chart_data.series.filter(series => series.config.type === 'line')

      if (this.chart_config.chartType === 'horizontal_bar') {
        // Calculate stacked maximums for each offset-axis combination
        Object.entries(barSeriesByOffsetAndAxis).forEach(([key, seriesGroup]) => {
          const [_, yAxis] = key.split('-')

          // Calculate max value accounting for stacking
          const stackedMax = data.map(d => {
            let sum = 0
            seriesGroup.forEach(series => {
              const value = d[series.config.label]
              if (value !== null && !isNaN(value)) {
                sum += value
              }
            })
            return sum
          })

          const maxForGroup = d3.max(stackedMax) || 1

          if (yAxis === 'left') {
            yMaxLeft = Math.max(yMaxLeft, maxForGroup)
          } else {
            yMaxRight = Math.max(yMaxRight, maxForGroup)
          }
        })

        // Add check for line series max values
        lineSeries.forEach(series => {
          const yAxis = series.config.yAxis || 'left'
          const maxLineValue = d3.max(data, d => d[series.config.label]) || 0

          if (yAxis === 'left') {
            yMaxLeft = Math.max(yMaxLeft, maxLineValue)
          } else {
            yMaxRight = Math.max(yMaxRight, maxLineValue)
          }
        })

        xScale = d3.scaleLinear()
          .domain([0, yMaxLeft])
          .nice()
          .range([0, width])

        // Create Y scales for horizontal bar chart
        yScales['left'] = d3.scaleBand()
          .domain(d3.range(data.length))
          .range([0, height])
          .padding(0.1)

        if (yMaxRight > 1) {
          yScales['right'] = d3.scaleLinear()
            .domain([0, yMaxRight])
            .nice()
            .range([height, 0])
        }
      } else {
        // For Vertical Bar Charts
        // Define X Scale (Band)
        xScale = d3.scaleBand()
          .domain(data.map((d, i) => i))
          .range([0, width])
          .padding(0.1)

        // Apply max bar width constraint if specified
        if (this.chart_config.maxBarWidth) {
          const maxBarWidth = this.chart_config.maxBarWidth
          const calculatedBandwidth = xScale.bandwidth()

          if (calculatedBandwidth > maxBarWidth) {
            // Adjust padding to limit the bar width
            const availableWidth = width
            const numBars = data.length
            const idealPadding = 1 - (maxBarWidth * numBars / availableWidth)

            // Make sure padding is between 0 and 1
            const newPadding = Math.min(Math.max(idealPadding, 0), 0.9)
            xScale.padding(newPadding)
          }
        }

        // Calculate stacked maximums for each offset-axis combination
        Object.entries(barSeriesByOffsetAndAxis).forEach(([key, seriesGroup]) => {
          const [_, yAxis] = key.split('-')

          // Calculate max value accounting for stacking
          const stackedMax = data.map(d => {
            let sum = 0
            seriesGroup.forEach(series => {
              const value = d[series.config.label]
              if (value !== null && !isNaN(value)) {
                sum += value
              }
            })
            return sum
          })

          const maxForGroup = d3.max(stackedMax) || 1

          if (yAxis === 'left' || yAxis === 'left') {
            yMaxLeft = Math.max(yMaxLeft, maxForGroup)
          } else {
            yMaxRight = Math.max(yMaxRight, maxForGroup)
          }
        })

        // Add check for line series max values
        lineSeries.forEach(series => {
          const yAxis = series.config.yAxis || 'left'
          const maxLineValue = d3.max(data, d => d[series.config.label]) || 0

          if (yAxis === 'left') {
            yMaxLeft = Math.max(yMaxLeft, maxLineValue)
          } else {
            yMaxRight = Math.max(yMaxRight, maxLineValue)
          }
        })

        // Compare with goal_target if applicable
        if (this.chart_data.goal_target) {
          yMaxLeft = Math.max(yMaxLeft, this.chart_data.goal_target)
        }

        // Ensure yMaxLeft and yMaxRight have sensible defaults
        yMaxLeft = yMaxLeft > 0 ? yMaxLeft : 1
        yMaxRight = yMaxRight > 0 ? yMaxRight : 1

        // Create Y scales for vertical bar chart
        yScales['left'] = d3.scaleLinear()
          .domain([0, yMaxLeft])
          .nice()
          .range([height, 0])

        if (yMaxRight > 1) {
          yScales['right'] = d3.scaleLinear()
            .domain([0, yMaxRight])
            .nice()
            .range([height, 0])
        }
      }

      // Return scales along with number of series and yMax values
      return { xScale, yScales, numSeries, yMaxLeft, yMaxRight }
    },
    getAdjustedTickValues(data, totalAvailableSpace, labelFontSize, labelRotation = 0, axis = 'x') {
      const minLabelSpacing = this.chart_config.labelDisplayConfig.minLabelSpacing || 10
      const tempSvg = d3.select(this.$refs.chart).append('svg').attr('visibility', 'hidden')
      const tempG = tempSvg.append('g')
      const labelSizes = data.map((d) => {
        const textElement = tempG.append('text')
          .style('font-size', `${labelFontSize}px`)
          .text(d.label)
        const bbox = textElement.node().getBBox()
        textElement.remove()
        let size
        if (axis === 'x') {
          size = bbox.width
          if (labelRotation !== 0) {
            const radians = Math.abs(labelRotation) * (Math.PI / 180)
            size = bbox.width * Math.cos(radians) + bbox.height * Math.sin(radians)
          }
        } else {
          size = bbox.height
        }
        return size
      })
      tempSvg.remove()
      const maxLabelSize = d3.max(labelSizes)
      const estimatedLabelSize = maxLabelSize + minLabelSpacing
      const maxLabels = Math.floor(totalAvailableSpace / estimatedLabelSize)
      let tickValues = []
      if (maxLabels >= data.length) {
        tickValues = d3.range(data.length)
      } else {
        const step = Math.ceil(data.length / maxLabels)
        tickValues = d3.range(0, data.length, step)
      }
      return tickValues
    },
    getAdjustedAxisTickValuesForContinuousScale(scale, axisLength, fontSize) {
      const minLabelSpacing = (this.chart_config.labelDisplayConfig.minLabelSpacing * 4) || 10
      const maxTicks = Math.floor(axisLength / (fontSize + minLabelSpacing))
      let tickValues = scale.ticks()
      if (tickValues.length > maxTicks) {
        const step = Math.ceil(tickValues.length / maxTicks)
        tickValues = tickValues.filter((d, i) => i % step === 0)
      }
      return tickValues
    },
    drawAxes(svg, scales, width, height, data) {
      const { xScale, yScales } = scales
      const yAxesConfig = this.chart_config.yAxes || []
      const maxHorizontalLabelLength = this.margin.left/7
      if (this.chart_config.chartType === 'horizontal_bar') {
        const yAxisTickValues = yScales['left'].domain()
        scales.yAxisTickValues = yAxisTickValues

        // Left Y Axis
        svg.append('g')
          .call(
            d3.axisLeft(yScales['left'])
              .tickSize(0)
              .tickPadding(6)
              .tickValues(yAxisTickValues)
              .tickFormat((d) => {
                const label = data[d]?.label || ''
                const formattedLabel = yAxesConfig[0]?.tickFormat
                  ? yAxesConfig[0].tickFormat(label)
                  : label
                return this.truncateLabel(
                    formattedLabel,
                    maxHorizontalLabelLength  )
              })
          )
          .selectAll('text')
          .style('font-size', `${yAxesConfig[0]?.fontSize || 12}px`)

        const xAxisTickValues = this.getAdjustedAxisTickValuesForContinuousScale(
          xScale,
          width,
          this.chart_config.xAxis.fontSize || 12
        )
        scales.xAxisTickValues = xAxisTickValues

        svg.append('g')
          .attr('transform', `translate(0,${height})`)
          .call(
            d3.axisBottom(xScale)
              .tickValues(xAxisTickValues)
              .tickFormat(
                this.chart_config.xAxis.tickFormat || ((d) => this.formatDataValue(d))
              )
          )
          .selectAll('text')
          .style('text-anchor', 'middle')
          .style('font-size', `${this.chart_config.xAxis.fontSize || 12}px`)
      } else { // Vertical bar chart
        const xAxisTickValues = this.getAdjustedTickValues(
          data,
          width,
          this.chart_config.xAxis.fontSize || 12,
          this.chart_config.xAxis.labelRotation || 0,
          'x'
        )
        scales.xAxisTickValues = xAxisTickValues

        // X Axis
        svg.append('g')
          .attr('transform', `translate(0,${height})`)
          .call(
            d3.axisBottom(xScale)
              .tickValues(xAxisTickValues)
              .tickFormat((d) => {
                const label = data[d]?.label || ''
                return this.chart_config.xAxis.tickFormat
                  ? this.chart_config.xAxis.tickFormat(label)
                  : label
              })
          )
          .selectAll('text')
          .attr('transform', `rotate(${this.chart_config.xAxis.labelRotation || 0})`)
          .style('text-anchor', this.chart_config.xAxis.labelRotation ? 'end' : 'middle')
          .style('font-size', `${this.chart_config.xAxis.fontSize || 12}px`)

        // Left Y Axis
        if (yScales['left']) {
          const yAxisTickValuesLeft = this.getAdjustedAxisTickValuesForContinuousScale(
            yScales['left'],
            height,
            yAxesConfig.find(axis => axis.position === 'left')?.fontSize || 12
          )
          scales.yAxisTickValuesLeft = yAxisTickValuesLeft

          svg.append('g')
            .call(
              d3.axisLeft(yScales['left'])
                .tickValues(yAxisTickValuesLeft)
                .tickFormat(
                  yAxesConfig.find(axis => axis.position === 'left')?.tickFormat || ((d) => this.formatDataValue(d))
                )
            )
            .selectAll('text')
            .style('font-size', `${yAxesConfig.find(axis => axis.position === 'left')?.fontSize || 12}px`)
        }

        // Right Y Axis
        if (yScales['right']) {
          const yAxisTickValuesRight = this.getAdjustedAxisTickValuesForContinuousScale(
            yScales['right'],
            height,
            yAxesConfig.find(axis => axis.position === 'right')?.fontSize || 12
          )
          scales.yAxisTickValuesRight = yAxisTickValuesRight

          svg.append('g')
            .attr('transform', `translate(${width},0)`)
            .call(
              d3.axisRight(yScales['right'])
                .tickValues(yAxisTickValuesRight)
                .tickFormat(
                  yAxesConfig.find(axis => axis.position === 'right')?.tickFormat || ((d) => this.formatDataValue(d))
                )
            )
            .selectAll('text')
            .style('font-size', `${yAxesConfig.find(axis => axis.position === 'right')?.fontSize || 12}px`)
        }
      }
    },
    drawBars(svg, xScale, yScales, numSeries, data) {
      const colors = d3.schemeCategory10
      const chartType = this.chart_config.chartType

      // Group series by offset and axis to handle stacking
      const barSeriesByOffsetAndAxis = {}

      this.chart_data.series.forEach((series, idx) => {
        if (series.config.type !== 'line') {
          const offset = series.config.offset || 0
          const yAxis = series.config.yAxis || 'left'
          const key = `${offset}-${yAxis}`

          if (!barSeriesByOffsetAndAxis[key]) {
            barSeriesByOffsetAndAxis[key] = []
          }

          barSeriesByOffsetAndAxis[key].push({
            series,
            idx
          })
        }
      })

      // Calculate the number of bar groups (unique offset-axis combinations)
      const uniqueBarGroups = Object.keys(barSeriesByOffsetAndAxis).length

      // Calculate max offset to determine bandwidth
      const maxOffset = Math.max(...this.chart_data.series
        .filter(series => series.config.type !== 'line')
        .map(series => series.config.offset || 0)) + 1

      // Define bandwidth per group based on the number of unique bar groups
      const adjustedBandwidth = (chartType === 'horizontal_bar'
        ? yScales['left'].bandwidth()
        : xScale.bandwidth()
      ) / maxOffset

      // Process each group of bars with the same offset and axis
      Object.entries(barSeriesByOffsetAndAxis).forEach(([key, seriesGroup]) => {
        const [offset, yAxis] = key.split('-')
        const numOffset = parseInt(offset, 10)

        // Prepare stacked data for this group
        const stackedData = data.map((d, dataIndex) => {
          const stackPoint = { dataIndex, index: dataIndex, ...d }

          // Calculate cumulative values for stacking
          let cumulative = 0
          seriesGroup.forEach(({ series }) => {
            const value = d[series.config.label]
            if (value !== null && !isNaN(value)) {
              stackPoint[`${series.config.label}_start`] = cumulative
              stackPoint[`${series.config.label}_end`] = cumulative + value
              cumulative += value
            } else {
              stackPoint[`${series.config.label}_start`] = null
              stackPoint[`${series.config.label}_end`] = null
            }
          })

          return stackPoint
        })

        // Draw bars for each series in this group
        seriesGroup.forEach(({ series, idx }) => {
          if (chartType === 'horizontal_bar') {
            svg.selectAll(`.bar-${idx}`)
              .data(stackedData)
              .enter()
              .append('rect')
              .attr('class', `bar-${idx}`)
              .attr('y', (d) => yScales['left'](d.index) + numOffset * adjustedBandwidth)
              .attr('x', (d) => xScale(d[`${series.config.label}_start`] || 0))
              .attr('height', adjustedBandwidth - 1)
              .attr('width', (d) => {
                if (d[`${series.config.label}_start`] === null) return 0
                return xScale(d[`${series.config.label}_end`]) - xScale(d[`${series.config.label}_start`])
              })
              .attr('fill', (d) => d.color || series.config.backgroundColor || colors[idx % colors.length])
              .attr('stroke', (d) => {
                const fillColor = d.color || series.config.backgroundColor || colors[idx % colors.length]
                const fillColorObj = d3.color(fillColor)
                if (!fillColorObj) {
                  console.warn('Invalid color:', fillColor)
                  return '#000000'  // Return black as fallback
                }
                fillColorObj.opacity = 1
                return fillColorObj.toString()
              })
              .attr('stroke-width', 1)
              .attr('data-series-label', series.config.label)
              .on('click', (event, d) => {
                this.$emit('bar-click', d)
              })
          } else {
            svg.selectAll(`.bar-${idx}`)
              .data(stackedData)
              .enter()
              .append('rect')
              .attr('class', `bar-${idx}`)
              .attr('x', (d) => xScale(d.index) + numOffset * adjustedBandwidth)
              .attr('y', (d) => {
                if (d[`${series.config.label}_end`] === null) return yScales[yAxis](0)
                return yScales[yAxis](d[`${series.config.label}_end`])
              })
              .attr('width', Math.max(adjustedBandwidth - (this.chart_config.barGap || 1), 0))
              .attr('height', (d) => {
                if (d[`${series.config.label}_start`] === null) return 0
                return yScales[yAxis](d[`${series.config.label}_start`]) - yScales[yAxis](d[`${series.config.label}_end`])
              })
              .attr('fill', (d) => d.color || series.config.backgroundColor || colors[idx % colors.length])
              .attr('stroke', (d) => {
                const fillColor = d.color || series.config.backgroundColor || colors[idx % colors.length]
                const fillColorObj = d3.color(fillColor)
                if (!fillColorObj) {
                  console.warn('Invalid color:', fillColor)
                  return '#000000'  // Return black as fallback
                }
                fillColorObj.opacity = 1
                return fillColorObj.toString()
              })
              .attr('stroke-width', 1)
              .attr('data-series-label', series.config.label)
              .on('click', (event, d) => {
                this.$emit('bar-click', d)
              })
          }

          // Remove bars with null values
          svg.selectAll(`.bar-${idx}`).filter((d) =>
            d[`${series.config.label}_start`] === null ||
            d[`${series.config.label}_end`] === null
          ).remove()
        })
      })
    },
    drawLines(svg, scales, data) {
      const { xScale, yScales } = scales
      const chartType = this.chart_config.chartType
      const lineSeries = this.chart_data.series.filter(series => series.config.type === 'line')
      const colors = d3.schemeCategory10
      lineSeries.forEach((series, idx) => {
        const offset = series.config.offset || 0
        const yAxis = series.config.yAxis
        const line = d3.line()
          .defined((d) => d[series.config.label] !== null)
          .x((d, i) => {
            return chartType === 'horizontal_bar'
              ? scales.xScale(d[series.config.label])
              : xScale(i) + xScale.bandwidth() * (offset + 0.5) // Center the line point based on offset
          })
          .y((d) => {
            return chartType === 'horizontal_bar'
              ? yScales[yAxis](d.index) + yScales[yAxis].bandwidth() / 2
              : yScales[yAxis](d[series.config.label])
          })
          .curve(d3.curveMonotoneX)

        svg.append('path')
          .datum(data)
          .attr('fill', 'none')
          .attr('stroke', series.config.backgroundColor || colors[idx % colors.length])
          .attr('stroke-width', 2)
          .attr('d', line)

        svg.selectAll(`.line-point-${idx}`)
          .data(data)
          .enter()
          .append('circle')
          .attr('class', `line-point-${idx}`)
          .attr('cx', (d, i) => {
            return chartType === 'horizontal_bar'
              ? scales.xScale(d[series.config.label])
              : xScale(i) + xScale.bandwidth() * (offset + 0.5)
          })
          .attr('cy', (d) => {
            return chartType === 'horizontal_bar'
              ? yScales[yAxis](d.index) + yScales[yAxis].bandwidth() / 2
              : yScales[yAxis](d[series.config.label])
          })
          .attr('r', 3)
          .attr('fill', series.config.backgroundColor || colors[idx % colors.length])
          .on('click', (event, d) => {
            this.$emit('line-click', d)
          })

          svg.selectAll(`.line-point-${idx}`).filter((d,i)=> d[series.config.label] === null).remove();

      })
    },
    addDataLabels(svg, scales, data) {
      const { xScale, yScales } = scales
      const chartType = this.chart_config.chartType
      const formatDataValue = this.formatDataValue.bind(this) // Bind this for use in callbacks
      // Set a smaller font size for data labels (was using labelDisplayConfig.labelFontSize)
      const labelFontSize = 12 // Smaller size than default
      const labelDisplayConfig = this.chart_config.labelDisplayConfig

      // Group series by offset and axis as we did in drawBars
      const barSeriesByOffsetAndAxis = {}
      const barSeries = this.chart_data.series.filter(series => series.config.type !== 'line')

      barSeries.forEach((series, idx) => {
        const offset = series.config.offset || 0
        const yAxis = series.config.yAxis || 'left'
        const key = `${offset}-${yAxis}`

        if (!barSeriesByOffsetAndAxis[key]) {
          barSeriesByOffsetAndAxis[key] = []
        }

        barSeriesByOffsetAndAxis[key].push({
          series,
          idx
        })
      })

      const colors = d3.schemeCategory10
      const maxOffset = Math.max(...barSeries.map(series => series.config.offset || 0))

      // Determine which data points to label
      let labelIndices
      if (chartType === 'horizontal_bar') {
        labelIndices = scales.yAxisTickValues
      } else {
        labelIndices = scales.xAxisTickValues
      }
      const filteredData = labelIndices.map(i => data[i])

      // Process each series group with the same offset and axis
      Object.entries(barSeriesByOffsetAndAxis).forEach(([key, seriesGroup]) => {
        const [offset, yAxis] = key.split('-')
        const numOffset = parseInt(offset, 10)

        // Prepare stacked data for labeling
        const stackedData = filteredData.map(d => {
          const stackPoint = { ...d }

          let cumulative = 0
          seriesGroup.forEach(({ series }) => {
            const value = d[series.config.label]
            if (value !== null && !isNaN(value)) {
              stackPoint[`${series.config.label}_start`] = cumulative
              stackPoint[`${series.config.label}_end`] = cumulative + value
              cumulative += value
            } else {
              stackPoint[`${series.config.label}_start`] = null
              stackPoint[`${series.config.label}_end`] = null
            }
          })

          return stackPoint
        })

        // Add labels for each series in this group
        seriesGroup.forEach(({ series, idx }) => {
          const fillColor = series.config.backgroundColor || colors[idx % colors.length]
          const estimateTextWidth = this.estimateTextWidth.bind(this)

          if (chartType === 'horizontal_bar') {
            svg.selectAll(`.label-${idx}`)
              .data(stackedData)
              .enter()
              .append('g')
              .attr('class', `label-${idx}`)
              .attr('transform', d => {
                const band = yScales['left'].bandwidth() / (maxOffset + 1)
                // Position label inside or outside bar based on available space
                const value = d[series.config.label]
                const barWidth = xScale(d[`${series.config.label}_end`]) - xScale(d[`${series.config.label}_start`])
                const textWidth = estimateTextWidth(formatDataValue(value), labelFontSize)
                const x = (barWidth > textWidth + 10)
                  ? xScale(d[`${series.config.label}_start`]) + barWidth / 2
                  : xScale(d[`${series.config.label}_end`]) + 5
                const y = yScales['left'](d.index) + band/2 + numOffset * band
                return `translate(${x}, ${y})`
              })
              .each(function(d) {
                // Add text with background for better visibility
                const g = d3.select(this)
                const value = d[series.config.label]
                if (value === null || isNaN(value)) return

                const formattedValue = formatDataValue(value)
                const barWidth = xScale(d[`${series.config.label}_end`]) - xScale(d[`${series.config.label}_start`])
                const textWidth = estimateTextWidth(formattedValue, labelFontSize)

                // Text label
                g.append('text')
                  .attr('text-anchor', (barWidth > textWidth + 10) ? 'middle' : 'start')
                  .attr('dominant-baseline', 'middle')
                  .text(formattedValue)
                  .style('font-size', `${labelFontSize}px`)
                  .style('font-weight', 'bold')
                  .style('fill', 'black')
                  .style('paint-order', 'stroke')
                  .style('stroke', 'white')
                  .style('stroke-width', '2px')
              })
          } else {
            svg.selectAll(`.label-${idx}`)
              .data(stackedData)
              .enter()
              .append('g')
              .attr('class', `label-${idx}`)
              .attr('transform', d => {
                const x = xScale(d.index) + xScale.bandwidth() * ((numOffset + 0.5) / (maxOffset + 1))
                return `translate(${x}, 0)`
              })
              .each(function(d) {
                const value = d[series.config.label]
                if (value === null || isNaN(value)) return

                const barHeight = yScales[yAxis](d[`${series.config.label}_start`]) - yScales[yAxis](d[`${series.config.label}_end`])
                const textHeight = labelFontSize + 2 // Approximate font height
                const barTop = yScales[yAxis](d[`${series.config.label}_end`])
                const barMiddle = barTop + barHeight / 2

                const g = d3.select(this)
                let y

                if (barHeight > textHeight + 10) {
                  // Enough space to place label inside bar
                  y = barMiddle
                } else if (barHeight > 8) {
                  // Place label above bar
                  y = barTop - 5
                } else {
                  // Too small: don't render label at all
                  g.remove()
                  return
                }

                // Add text label with improved visibility
                g.append('text')
                  .attr('y', y)
                  .attr('text-anchor', 'middle')
                  .attr('dominant-baseline', (barHeight > textHeight + 10) ? 'middle' : 'auto')
                  .text(formatDataValue(value))
                  .style('font-size', `${labelFontSize}px`)
                  .style('font-weight', 'bold')
                  .style('fill', 'black')
                  .style('paint-order', 'stroke')
                  .style('stroke', 'white')
                  .style('stroke-width', '2px')
              })
          }

          // Remove labels for null values
          svg.selectAll(`.label-${idx}`).filter(d =>
            d[`${series.config.label}_start`] === null ||
            d[`${series.config.label}_end`] === null
          ).remove()
        })
      })
    },
addTooltip(svg) {
  const tooltip = d3.select(this.$refs.chart)
    .append('div')
    .attr('class', 'tooltip')
    .style('position', 'fixed')
    .style('visibility', 'hidden')
    .style('z-index', '9999')
    .style('background', '#fff')
    .style('border', '1px solid #ccc')
    .style('padding', '8px')
    .style('font-size', '12px')
    .style('pointer-events', 'none')
    .style('border-radius', '4px')
    .style('box-shadow', '0px 2px 4px rgba(0,0,0,0.1)')
    .style('max-width', '300px')
    .style('line-height', '1.4')

  const formatTooltipContent = (d, seriesLabel, tooltipFormatter) => {
    if (tooltipFormatter) {
      return tooltipFormatter(d);
    }

    const value = d[seriesLabel]
    return `<strong>${seriesLabel}</strong><br/>${d.label}: ${this.formatDataValue(value)}`;
  };

  this.chart_data.series.forEach((series, idx) => {
    if (series.config.type !== 'line') {
      svg.selectAll(`.bar-${idx}`)
        .on('mouseover', (event, d) => {
          // Get the series label from the bar element
          const seriesLabel = d3.select(event.currentTarget).attr('data-series-label')

          const content = formatTooltipContent(d, seriesLabel, series.config.tooltip?.formatter)
          tooltip
            .style('visibility', 'visible')
            .html(content)

          const bar = d3.select(event.currentTarget)
          const originalFill = bar.attr('fill')
          bar.attr('data-original-fill', originalFill)
          const fillColorObj = d3.color(originalFill)
          fillColorObj.opacity = 0.3
          bar.attr('fill', fillColorObj)
        })
        .on('mousemove', (event) => {
          tooltip
            .style('top', `${event.clientY - 10}px`)
            .style('left', `${event.clientX + 10}px`)
        })
        .on('mouseout', (event) => {
          tooltip.style('visibility', 'hidden')
          const bar = d3.select(event.currentTarget)
          const originalFill = bar.attr('data-original-fill')
          if (originalFill) {
            bar.attr('fill', originalFill)
            bar.attr('data-original-fill', null)
          }
        })
    }
  })

  this.chart_data.series.filter(series => series.config.type === 'line').forEach((series, idx) => {
    svg.selectAll(`.line-point-${idx}`)
      .on('mouseover', (event, d) => {
        const content = formatTooltipContent(d, series.config.label, series.config.tooltip?.formatter)
        tooltip
          .style('visibility', 'visible')
          .html(content)

        d3.select(event.currentTarget)
          .attr('r', 6)
      })
      .on('mousemove', (event) => {
        tooltip
          .style('top', `${event.clientY - 10}px`)
          .style('left', `${event.clientX + 10}px`)
      })
      .on('mouseout', (event) => {
        tooltip.style('visibility', 'hidden')
        d3.select(event.currentTarget)
          .attr('r', 3)
      })
  })
},
    addLegend(svg, width) {
      const numSeries = this.chart_data.series.length
      const colors = d3.schemeCategory10

      const legendSpacing = this.chart_config.legend.spacing || 15

      if (this.chart_config.legend.display && numSeries > 1) {
        const legend = svg.selectAll('.legend')
          .data(this.chart_data.series)
          .enter()
          .append('g')
          .attr('class', 'legend')

        if (this.chart_config.legend.position === 'right') {
          // Vertical legend on the right side
          const legendX = width + 20 // Start 20px to the right of the chart

          legend.append('rect')
            .attr('x', legendX)
            .attr('y', 0)
            .attr('width', 18)
            .attr('height', 18)
            .style('fill', (d, i) => d.config.backgroundColor || colors[i % colors.length])

          legend.append('text')
            .attr('x', legendX + 24)  // Text 24px from legend start (18 for rect + 6 padding)
            .attr('y', 9)
            .attr('dy', '.35em')
            .style('text-anchor', 'start')
            .text((d) => d.config.label)
            .style('font-size', '12px')
            .style('font-weight', '500')

          // Position each legend item vertically with spacing
          legend.attr('transform', (d, i) => `translate(0, ${i * 25})`)
        }
        else if (this.chart_config.legend.position === 'subtitle') {
          // Calculate title width if it exists
          let titleWidth = 0
          if (this.chart_config.title) {
            const tempSvg = d3.select(this.$refs.chart).append('svg').attr('visibility', 'hidden')
            const tempText = tempSvg.append('text')
              .style('font-size', '20px')
              .style('font-weight', '600')
              .style('font-family', '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif')
              .text(toProperCase(this.chart_config.title))
            titleWidth = tempText.node().getBBox().width
            tempSvg.remove()
          }

          // Adjust the vertical positioning of the legend
          legend.append('rect')
            .attr('x', -this.margin.left + this.$el.clientWidth/2)
            .attr('y', -this.margin.top + 20)
            .attr('width', 18)
            .attr('height', 18)
            .style('fill', (d, i) => d.config.backgroundColor || colors[i % colors.length])

          legend.append('text')
            .attr('x', -this.margin.left + this.$el.clientWidth/2 + 24)
            .attr('y', -this.margin.top + 29)
            .attr('dy', '.35em')
            .style('text-anchor', 'start')
            .text((d) => d.config.label)
            .style('font-size', '12px')

          // First pass to calculate total xOffset
          let totalXOffset = 0
          const textWidths = []

          legend.each(function() {
            const text = d3.select(this).select('text')
            const textWidth = text.node().getBBox().width + 30
            textWidths.push(textWidth)
            totalXOffset += textWidth
          })

          // Calculate available width (avoiding title overlap)
          const chartCenterX = this.$el.clientWidth / 2
          const availableWidth = this.$el.clientWidth - Math.max(titleWidth + 40, 0)

          // Check if legend would overlap with title
          const legendStartX = chartCenterX - (totalXOffset / 2)
          const legendEndX = chartCenterX + (totalXOffset / 2)
          const titleStartX = chartCenterX - (titleWidth / 2)
          const titleEndX = chartCenterX + (titleWidth / 2)

          let shiftLeft = totalXOffset / 2

          // If legend overlaps with title, shift it down
          if (this.chart_config.title &&
              ((legendStartX < titleEndX && legendEndX > titleStartX) ||
               totalXOffset > availableWidth)) {
            // Move legend below the title by adjusting y position
            legend.selectAll('rect')
              .attr('y', -this.margin.top + 45)

            legend.selectAll('text')
              .attr('y', -this.margin.top + 54)
          }

          // Second pass to set transform with left shift
          let xOffset = 0
          legend.attr('transform', function(d, i) {
            const transform = `translate(${xOffset - shiftLeft},0)`
            xOffset += textWidths[i]
            return transform
          })
        }
        else {
          // Default legend position
          legend.append('rect')
            .attr('x', 3)
            .attr('y', 0)
            .attr('width', 18)
            .attr('height', 18)
            .style('fill', (d, i) => d.config.backgroundColor || colors[i % colors.length])

          legend.append('text')
            .attr('x', 24)
            .attr('y', 9)
            .attr('dy', '.35em')
            .style('text-anchor', 'start')
            .text((d) => d.config.label)
            .style('font-size', '12px')

          legend.attr('transform', (d, i) => `translate(0, ${i * 20})`)
        }
      }
    },
    estimateTextWidth(text, fontSize) {
      // Rough estimate of text width based on font size and string length
      return text.toString().length * fontSize * 0.6
    },
    getContrastColor(hexColor) {
      // Convert hex to RGB
      let rgb = d3.color(hexColor)

      // Calculate luminance - a weighted average giving green more influence
      const luminance = 0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b

      // Return black or white based on background luminance
      return luminance > 400 ? '#000000' : '#ffffff'
    }
  },
}
</script>

<style scoped>
.tooltip {
  pointer-events: none;
}
</style>
